// Cybox Product Finder berekening (Bas Hendriks, mrt 2026).
// Spec: docs/reference/cybox-product-finder-calculation.md
//
// Open punten (Cybox doc, geel): body_shape bij puppies/dracht/lactatie → we slaan
// body_shape over voor puppy + pregnant/lactating; breed correction wel toegepast.

window.WolforkQuizAdh = {
  ACTIVITY_FACTORS: {
    low: 0.73,
    medium: 0.85,
    high: 0.96,
    very_high: 1.23,
  },

  BODY_SHAPE_FACTORS: {
    underweight: 1.05,
    ideal: 1.0,
    overweight: 0.8,
    obese: 0.69,
  },

  LACTATION_WEEK_FACTORS: {
    1: 0.75,
    2: 0.95,
    3: 1.1,
    4: 1.2,
  },

  DEFAULT_ENERGY_KCAL_PER_KG: 1920,
  PUPPY_ENERGY_KCAL_PER_KG: 2000,
  DEFAULT_PACK_GRAMS: 2100,

  getAgeMonths(state) {
    if (!state?.age_value) return null;
    return state.age_unit === 'jaren'
      ? state.age_value * 12
      : state.age_value;
  },

  getAdultWeightKg(state) {
    const expected = parseFloat(state.expected_weight_kg);
    if (expected > 0) return expected;
    const breedAvg = parseFloat(state.breed_avg_adult_weight_kg ?? state.breed_baseline_weight_kg);
    if (breedAvg > 0) return breedAvg;
    const current = parseFloat(state.weight_kg);
    return current > 0 ? current : null;
  },

  getLifestageThresholds(adultWeightKg) {
    const w = adultWeightKg || 25;
    if (w <= 10) return { puppyMaxMonths: 10, seniorMinYears: 10 };
    if (w <= 25) return { puppyMaxMonths: 12, seniorMinYears: 9 };
    if (w <= 40) return { puppyMaxMonths: 15, seniorMinYears: 8 };
    return { puppyMaxMonths: 18, seniorMinYears: 7 };
  },

  resolveLifestage(state) {
    const ageMonths = this.getAgeMonths(state);
    const adultWeight = this.getAdultWeightKg(state);
    if (ageMonths == null) return 'adult';

    const { puppyMaxMonths, seniorMinYears } = this.getLifestageThresholds(adultWeight);

    if (state.pregnant === true || state.nursing === true) return 'mother';
    if (ageMonths < puppyMaxMonths) return 'puppy';
    if (ageMonths >= seniorMinYears * 12) return 'senior';
    if (ageMonths <= 24) return 'young_adult';
    return 'adult';
  },

  calculateMer(state) {
    const weight = parseFloat(state.weight_kg);
    if (!weight || weight <= 0) return null;

    const wPow = Math.pow(weight, 0.75);

    if (state.nursing === true) {
      const pups = Math.min(15, Math.max(1, parseInt(state.puppies_count, 10) || 1));
      const week = Math.min(4, Math.max(1, parseInt(state.nursing_weeks, 10) || 1));
      const lactFactor = this.LACTATION_WEEK_FACTORS[week] || 1.2;
      const base = 145 * wPow;
      const extra = pups <= 4
        ? (24 * pups) * weight * lactFactor
        : (96 + 12 * pups) * weight * lactFactor;
      return base + extra;
    }

    if (state.pregnant === true) {
      const weeks = parseInt(state.pregnancy_weeks, 10) || 1;
      const factor = weeks <= 4 ? 132 : 158;
      return factor * wPow;
    }

    const lifestage = this.resolveLifestage(state);
    let merFactor = 110;
    if (lifestage === 'young_adult') merFactor = 130;
    if (lifestage === 'senior') merFactor = 95;

    return merFactor * wPow;
  },

  calculatePuppyDer(state) {
    const weight = parseFloat(state.weight_kg);
    const adultWeight = this.getAdultWeightKg(state);
    if (!weight || weight <= 0 || !adultWeight || adultWeight <= 0) return null;

    const ageMonths = this.getAgeMonths(state) || 0;
    const ageWeeks = ageMonths * (52 / 12);

    if (ageWeeks <= 7) {
      return 0.1 * weight * 25;
    }

    return (254.1 - (135 * weight) / adultWeight) * Math.pow(weight, 0.75);
  },

  applyDerFactors(baseDer, state, options = {}) {
    let der = baseDer;
    const activity = this.ACTIVITY_FACTORS[state.activity_level] ?? 0.85;
    der *= activity;

    if (state.sterilized === true) {
      der *= 0.89;
    }

    if (!options.skipBodyShape) {
      const body = this.BODY_SHAPE_FACTORS[state.body_shape] ?? 1.0;
      der *= body;
    }

    const breedFactor = parseFloat(state.breed_correction_factor) || 1;
    der *= breedFactor;

    return der;
  },

  calculateDer(state) {
    const lifestage = this.resolveLifestage(state);
    let base;

    if (lifestage === 'puppy') {
      base = this.calculatePuppyDer(state);
      if (base == null) return null;
      return this.applyDerFactors(base, state, { skipBodyShape: true });
    }

    const mer = this.calculateMer(state);
    if (mer == null) return null;

    const skipBody = state.pregnant === true || state.nursing === true;
    return this.applyDerFactors(mer, state, { skipBodyShape: skipBody });
  },

  defaultEnergyForRecipe(recipeHandle) {
    if (!recipeHandle) return this.DEFAULT_ENERGY_KCAL_PER_KG;
    const h = String(recipeHandle).toLowerCase();
    if (h.includes('puppy') || h.includes('pup')) return this.PUPPY_ENERGY_KCAL_PER_KG;
    return this.DEFAULT_ENERGY_KCAL_PER_KG;
  },

  resolveEnergyKcalPerKg(state, recipeMeta) {
    if (recipeMeta?.energy_kcal_per_kg) {
      return parseFloat(recipeMeta.energy_kcal_per_kg);
    }
    return this.defaultEnergyForRecipe(state.recipe);
  },

  resolvePackSizeGrams(recipeMeta) {
    const pack = parseInt(recipeMeta?.pack_size_g, 10);
    return pack > 0 ? pack : this.DEFAULT_PACK_GRAMS;
  },

  lookupRecipeMeta(recipeHandle) {
    if (!recipeHandle) return null;
    const ids = ['wf-recipe-products-data', 'wf-recipes-data', 'wf-recipes-full-data'];
    for (const id of ids) {
      try {
        const el = document.getElementById(id);
        if (!el) continue;
        const recipes = JSON.parse(el.textContent);
        const match = recipes.find((r) => (r.value || r.handle) === recipeHandle);
        if (match) return match;
      } catch { /* probeer volgende bron */ }
    }
    return null;
  },

  calculateEnergy(state) {
    const der = this.calculateDer(state);
    const mer = state.nursing === true || state.pregnant === true || this.resolveLifestage(state) === 'puppy'
      ? null
      : this.calculateMer(state);

    return {
      mer: mer != null ? Math.round(mer * 100) / 100 : null,
      der: der != null ? Math.round(der * 100) / 100 : null,
      lifestage: this.resolveLifestage(state),
    };
  },

  calculateAdh(state) {
    const der = this.calculateDer(state);
    if (!der || der <= 0) return null;

    const recipeMeta = this.lookupRecipeMeta(state.recipe);
    const kcalPerKg = this.resolveEnergyKcalPerKg(state, recipeMeta);
    const rawFraction = (parseInt(state.raw_percentage, 10) || 100) / 100;
    const grams = (der / kcalPerKg) * 1000 * rawFraction;

    return Math.round(grams);
  },

  calculateFrequencyDays(adhGrams, packSizeGrams) {
    const pack = packSizeGrams || this.DEFAULT_PACK_GRAMS;
    if (!adhGrams || adhGrams <= 0) return null;
    return Math.round((pack / adhGrams) * 10) / 10;
  },

  calculateKcalPerDay(state, adhGrams, recipeMeta) {
    if (!adhGrams) return null;
    const kcalPerKg = this.resolveEnergyKcalPerKg(state, recipeMeta);
    return Math.round((kcalPerKg / 1000) * adhGrams);
  },

  _selfTest() {
    const cases = [
      {
        name: 'Cybox export Benz (adult 28kg, ideal, medium)',
        state: {
          weight_kg: 28,
          age_value: 4,
          age_unit: 'jaren',
          body_shape: 'ideal',
          activity_level: 'medium',
          sterilized: false,
          raw_percentage: 100,
          recipe: 'adult-compleet-kipvrij',
        },
        energyKcalPerKg: 1920,
        expectDerMin: 1050,
        expectDerMax: 1200,
        expectGramsMin: 540,
        expectGramsMax: 640,
      },
      {
        name: 'Cybox export Sushi (puppy 33kg, adult 40kg, low)',
        state: {
          weight_kg: 33,
          expected_weight_kg: 40,
          age_value: 9,
          age_unit: 'maanden',
          body_shape: 'ideal',
          activity_level: 'low',
          sterilized: false,
          raw_percentage: 100,
          recipe: 'puppy-compleet-kipvrij',
        },
        energyKcalPerKg: 2000,
        expectDerMin: 1300,
        expectDerMax: 2100,
        expectGramsMin: 650,
        expectGramsMax: 1050,
      },
    ];

    return cases.map((c) => {
      const meta = { energy_kcal_per_kg: c.energyKcalPerKg };
      const energy = this.calculateEnergy(c.state);
      const adh = Math.round((energy.der / c.energyKcalPerKg) * 1000);
      const passDer = energy.der >= c.expectDerMin && energy.der <= c.expectDerMax;
      const passGrams = adh >= c.expectGramsMin && adh <= c.expectGramsMax;
      return {
        name: c.name,
        mer: energy.mer,
        der: energy.der,
        adh,
        pass: passDer && passGrams,
      };
    });
  },
};
